/**
 * Notification Preferences Hooks
 *
 * TanStack Query hooks for fetching and updating notification preferences.
 * Includes helper functions for effective preference resolution and category grouping.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "@/services/api/sdk.gen";
import type { UpdateNotificationPreferencesBody } from "@/services/api/types.gen";
import { logger } from "@/services/logger";
import { generateUUID } from "@/utils/uuid";

// ============================================================
// Types
// ============================================================

export interface NotificationTypePreference {
  enabled: boolean;
  showToast: boolean;
  showInPanel: boolean;
  emailEnabled: boolean;
}

export interface NotificationType {
  typeKey: string;
  displayName: string;
  description: string;
  category: string;
  defaultEnabled: boolean;
  defaultShowToast: boolean;
  defaultShowPanel: boolean;
  userOverride: Partial<NotificationTypePreference>;
}

export interface NotificationPreferences {
  globalEnabled: boolean;
  soundEnabled: boolean;
  desktopEnabled: boolean;
  quietHours: {
    enabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
  } | null;
  notificationTypes: NotificationType[];
}

type UpdatePreferencesPayload = UpdateNotificationPreferencesBody;

// ============================================================
// Hooks
// ============================================================

/**
 * Fetch notification preferences.
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: ["notification-preferences"],
    queryFn: async () => {
      const result = await getNotificationPreferences({ throwOnError: true });
      return result.data?.data as unknown as NotificationPreferences;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Update notification preferences with optimistic update + rollback.
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdatePreferencesPayload) => {
      await updateNotificationPreferences({
        throwOnError: true,
        body: payload,
      });
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: ["notification-preferences"],
      });

      const previousPrefs = queryClient.getQueryData<NotificationPreferences>([
        "notification-preferences",
      ]);

      if (previousPrefs) {
        queryClient.setQueryData<NotificationPreferences>(
          ["notification-preferences"],
          {
            ...previousPrefs,
            ...(payload.globalEnabled !== undefined && {
              globalEnabled: payload.globalEnabled,
            }),
            ...(payload.soundEnabled !== undefined && {
              soundEnabled: payload.soundEnabled,
            }),
            ...(payload.desktopEnabled !== undefined && {
              desktopEnabled: payload.desktopEnabled,
            }),
            notificationTypes: previousPrefs.notificationTypes.map((type) => {
              const override = payload.typeOverrides?.[type.typeKey];
              if (override) {
                return {
                  ...type,
                  userOverride: { ...type.userOverride, ...override },
                };
              }
              return type;
            }),
          },
        );
      }

      return { previousPrefs };
    },
    onError: (err, _payload, context) => {
      logger.error("Failed to update notification preferences", err as Error, {
        correlationId: generateUUID(),
      });
      if (context?.previousPrefs) {
        queryClient.setQueryData(
          ["notification-preferences"],
          context.previousPrefs,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["notification-preferences"],
      });
    },
  });
}

// ============================================================
// Helpers
// ============================================================

/**
 * Resolve effective preference value (user override falls back to default).
 */
export function getEffectivePreference(
  type: NotificationType,
  key: keyof NotificationTypePreference,
): boolean {
  if (type.userOverride[key] !== undefined) {
    return type.userOverride[key] as boolean;
  }

  switch (key) {
    case "enabled":
      return type.defaultEnabled;
    case "showToast":
      return type.defaultShowToast;
    case "showInPanel":
      return type.defaultShowPanel;
    case "emailEnabled":
      return false;
    default:
      return true;
  }
}

/**
 * Group notification types by category.
 */
export function groupByCategory(
  types: NotificationType[],
): Record<string, NotificationType[]> {
  const groups: Record<string, NotificationType[]> = {};
  for (const type of types) {
    const cat = type.category;
    if (!groups[cat]) {
      groups[cat] = [];
    }
    groups[cat].push(type);
  }
  return groups;
}

/**
 * Human-readable category names.
 */
export const CATEGORY_NAMES: Record<string, string> = {
  execution: "Workflow Execution",
  team: "Team Activity",
  system: "System",
};

/**
 * Categories that are system-only events (not user-facing notifications).
 * These are filtered out of the preferences UI.
 */
export const SYSTEM_ONLY_CATEGORIES = new Set(["asset"]);
